/* eslint-disable @typescript-eslint/no-shadow */
import React, { forwardRef } from 'react'
import type { StyleProp } from '@vtex/admin-ui'
import { IconDotsSixVertical, Text, color, csx } from '@vtex/admin-ui'
import { useFragment } from 'react-relay-offline'
import { graphql } from 'babel-plugin-relay/macro'

import type { ListItemService, ServiceInfo } from '../../shared/types'
import { Avatar } from '../Avatar'
import type { InStoreListItem_salesAssociate$key } from './__generated__/InStoreListItem_salesAssociate.graphql'
import { ElapsedTimer } from '../ElapsedTimer'
import { sizeToRem } from '../../../../core/theme/theme'
import DraggableCard from '../ListItem/DraggableCard'

const AVATAR_SIZE = 2

const textStyles: StyleProp = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const elapedtimerStyled: StyleProp = {
  position: 'absolute',
  top: 0,
  right: 0,
  fontSize: sizeToRem(0),
  backgroundColor: '$secondary',
  borderTopRightRadius: 'default',
  borderBottomLeftRadius: 'default',
  border: `1px ${color('$gray20')}`,
  borderStyle: 'none none solid solid',
  paddingX: '$space-1',
  paddingY: '$space-1',
}

const getItemInfoStyles = () => {
  return {
    width: `calc(100% - ${AVATAR_SIZE}rem - $space-4`,
  }
}

type Props<ItemType> = {
  item: ItemType
  dndType: string
  index: number
  avatarIconBgColor?: string
  onClick: (item: ItemType, index: number) => void
  testId?: string
  usedAsDragPreview?: boolean
}

type ItemData = {
  salesAssociate: InStoreListItem_salesAssociate$key
  service?: ListItemService
  displaydServiceInfo?: ServiceInfo
}

// We need to manually cast the `InStoreListItem` to keep
// the generics, because `forwardRef` narrows the
// type when returning the fowarded component
type InStoreListItemType = <ItemType extends ItemData>(
  props: Props<ItemType> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => JSX.Element

export const InStoreListItem = forwardRef(function InStoreListItem<
  ItemType extends ItemData
>(
  {
    item,
    dndType,
    index,
    avatarIconBgColor,
    onClick,
    testId,
    usedAsDragPreview,
  }: Props<ItemType>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const salesAssociate = useFragment<InStoreListItem_salesAssociate$key>(
    graphql`
      fragment InStoreListItem_salesAssociate on SalesAssociate {
        name
        avatar {
          url
        }
      }
    `,
    item.salesAssociate
  )

  const handleClick = () => {
    onClick(item, index)
  }

  return (
    <DraggableCard
      ref={ref}
      item={item}
      dndType={dndType}
      index={index}
      onClick={handleClick}
      className={csx({
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        paddingY: '$space-4',
      })}
      data-testid={testId}
      usedAsDragPreview={usedAsDragPreview}
    >
      <IconDotsSixVertical size="small" />
      <Avatar
        src={salesAssociate.avatar?.url}
        icon={item.displaydServiceInfo?.icon?.id}
        iconBgColor={avatarIconBgColor}
        size={AVATAR_SIZE}
        className={csx({ flexShrink: 0, paddingX: '$space-2' })}
      />
      <div className={csx({ getItemInfoStyles })}>
        <Text className={csx({ ...textStyles, text: '$body' })}>
          {salesAssociate.name}
        </Text>
        <p
          className={csx({
            ...textStyles,
            text: '$detail',
            color: '$secondary',
          })}
        >
          {item.displaydServiceInfo?.name}
        </p>
      </div>
      {item.service?.startDate && (
        <ElapsedTimer
          startTime={item.service.startDate}
          className={csx({ ...elapedtimerStyled })}
        />
      )}
    </DraggableCard>
  )
}) as InStoreListItemType
